import * as React from 'react';
import type { AnyFieldApi } from '@tanstack/react-form';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FormField, Input } from '../../../components/ui/FormField.js';
import { FormSelect } from '../../../components/ui/FormSelect.js';
import { FormActions } from '@repo/web-form';
import { useToast } from '../../../components/ui/Toast.js';
import { useCreatePidiScheme } from '../../../features/pidi/network';
import { agentHooks } from '../../../features/agents/hooks';
import { defineFormPage, useFormPage } from '@repo/form';
import { z } from 'zod';
import { Banner } from '../../../components/ui/Banner.js';
import { FormPageLayout } from '../../../components/layout/FormPageLayout';
import Decimal from 'decimal.js';
import { usePageTitle } from '../../../lib/hooks';

export const Route = createFileRoute('/_auth/pidi-schemes/new')({
  component: CreatePidiSchemePage,
});

const pidiSchemeSchema = z.object({
  displayName: z.string().min(1, 'Display name is required'),
  schemeNumber: z.string().min(1, 'Scheme number is required'),
  dailyAmountRupees: z.string().min(1, 'Daily amount is required'),
  durationDays: z.string().min(1, 'Duration is required'),
  agentId: z.string().min(1, 'Agent is required'),
  startDate: z.string().min(1, 'Start date is required'),
});

type FormValues = z.infer<typeof pidiSchemeSchema>;

const pidiSchemeFormConfig = defineFormPage<FormValues>({
  resource: 'pidi-schemes',
  defaultValues: {
    displayName: '',
    schemeNumber: '',
    dailyAmountRupees: '',
    durationDays: '100',
    agentId: '',
    startDate: new Date().toISOString().split('T')[0],
  },
  schema: pidiSchemeSchema,
});

function CreatePidiSchemePage() {
  usePageTitle('Create Pidi Scheme');
  const navigate = useNavigate();
  const { toast } = useToast();
  const createScheme = useCreatePidiScheme();
  const { data: agentsRes } = agentHooks.useList({ limit: 1000 });
  const agents = agentsRes?.data || [];

  const {
    form,
    isSubmitting,
    handleSubmit,
    fieldError: fe,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(pidiSchemeFormConfig, createScheme, {
    routeId: 'pidi-schemes/new',
    onBeforeSubmit: (values) => {
      const rupees = new Decimal(values.dailyAmountRupees);
      return {
        customerId: values.agentId,
        dailyAmountPaise: rupees.times(100).round().toNumber(),
        tenureDays: parseInt(values.durationDays),
        assignedAgentId: values.agentId || null,
        startDate: values.startDate,
      };
    },
    onSuccess: () => {
      clearDraft();
      toast({
        type: 'success',
        title: 'Success',
        description: 'Pidi daily collection scheme created successfully as DRAFT.',
      });
      navigate({ to: '/pidi-schemes' });
    },
    onError: () => {
      toast({
        type: 'error',
        title: 'Error',
        description: 'Failed to create pidi scheme. Please try again.',
      });
    },
  });

  return (
    <FormPageLayout
      title="Create Pidi Scheme"
      subtitle="Launch a high-frequency, daily-collection micro deposit scheme. Fill in the terms and assign a collection agent."
      backTo="/pidi-schemes"
      variant="card"
    >
      {hasDraft && (
        <div className="mb-6">
          <Banner
            variant="info"
            title="Restore Draft"
            description="You have an unsaved draft from a previous session. Would you like to restore it?"
            action={{
              label: 'Restore',
              onClick: () => {
                const draft = restoreDraft();
                 if (draft) {
                   Object.entries(draft).forEach(([key, val]) => {
                     form.setFieldValue(key as never, val as never);
                   });
                 }
                dismissDraft();
              },
            }}
            secondaryAction={{
              label: 'Discard',
              onClick: () => {
                clearDraft();
              },
            }}
          />
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 pt-4">
            <form.Field
              name="displayName"
              validators={{ onChange: ({ value }: { value: unknown }) => pidiSchemeSchema.shape.displayName.safeParse(value).success ? undefined : pidiSchemeSchema.shape.displayName.safeParse(value).error?.issues[0]?.message }}
              children={(field: AnyFieldApi) => (
                <FormField label="Scheme Display Name" required error={fe(field.state.meta)}>
                  <Input
                    placeholder="e.g. Daily Pigmy Savings 100 Days"
                    value={field.state.value}
                    onChange={e => field.handleChange(e.target.value)}
                  />
                </FormField>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <form.Field
                name="schemeNumber"
                validators={{ onChange: ({ value }: { value: unknown }) => pidiSchemeSchema.shape.schemeNumber.safeParse(value).success ? undefined : pidiSchemeSchema.shape.schemeNumber.safeParse(value).error?.issues[0]?.message }}
                children={(field: AnyFieldApi) => (
                  <FormField label="Scheme Number (Code)" required error={fe(field.state.meta)}>
                    <Input
                      placeholder="e.g. PIDI-2026-004"
                      value={field.state.value}
                      onChange={e => field.handleChange(e.target.value)}
                    />
                  </FormField>
                )}
              />

              <form.Field
                name="dailyAmountRupees"
                validators={{ onChange: ({ value }: { value: unknown }) => pidiSchemeSchema.shape.dailyAmountRupees.safeParse(value).success ? undefined : pidiSchemeSchema.shape.dailyAmountRupees.safeParse(value).error?.issues[0]?.message }}
                children={(field: AnyFieldApi) => (
                  <FormField label="Daily Amount (₹)" required error={fe(field.state.meta)}>
                    <Input
                      type="number"
                      placeholder="e.g. 100"
                      value={field.state.value}
                      onChange={e => field.handleChange(e.target.value)}
                    />
                  </FormField>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <form.Field
                name="durationDays"
                validators={{ onChange: ({ value }: { value: unknown }) => pidiSchemeSchema.shape.durationDays.safeParse(value).success ? undefined : pidiSchemeSchema.shape.durationDays.safeParse(value).error?.issues[0]?.message }}
                children={(field: AnyFieldApi) => (
                  <FormSelect
                    label="Duration (Days)"
                    value={field.state.value}
                    onChange={e => field.handleChange(e.target.value)}
                    options={[
                      { value: '100', label: '100 Days' },
                      { value: '200', label: '200 Days' },
                      { value: '365', label: '365 Days (1 Year)' },
                    ]}
                    error={fe(field.state.meta)}
                  />
                )}
              />

              <form.Field
                name="agentId"
                validators={{ onChange: ({ value }: { value: unknown }) => pidiSchemeSchema.shape.agentId.safeParse(value).success ? undefined : pidiSchemeSchema.shape.agentId.safeParse(value).error?.issues[0]?.message }}
                children={(field: AnyFieldApi) => (
                  <FormSelect
                    label="Assigned Agent"
                    value={field.state.value}
                    onChange={e => field.handleChange(e.target.value)}
                    options={agents.map((a: { id: string; fullName: string }) => ({ value: a.id, label: a.fullName }))}
                    error={fe(field.state.meta)}
                  />
                )}
              />
            </div>

            <form.Field
              name="startDate"
              validators={{ onChange: ({ value }: { value: unknown }) => pidiSchemeSchema.shape.startDate.safeParse(value).success ? undefined : pidiSchemeSchema.shape.startDate.safeParse(value).error?.issues[0]?.message }}
              children={(field: AnyFieldApi) => (
                <FormField label="Launch Date">
                  <Input
                    type="date"
                    value={field.state.value}
                    onChange={e => field.handleChange(e.target.value)}
                  />
                </FormField>
              )}
            />

            <FormActions onCancel={() => void navigate({ to: '/pidi-schemes' })} isSubmitting={isSubmitting} submitLabel="Create Scheme" />
          </form>
    </FormPageLayout>
  );
}
